import { useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Package, Pencil, Plus, Power, PowerOff } from 'lucide-react';
import { useState } from 'react';

import {
  getCatalogoFarmacosControllerListarQueryKey,
  getCatalogoFarmacosControllerObterQueryKey,
  useCatalogoFarmacosControllerAtualizarApresentacao,
  useCatalogoFarmacosControllerDesativarApresentacao,
} from '@/api/generated/admin-catalogo-farmacos/admin-catalogo-farmacos';
import type { ApresentacaoResponse, CatalogoFarmacoDetalheResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { ApresentacaoFormDialogo } from '@/features/admin/catalogo-farmacos/components/ApresentacaoFormDialogo';
import { EstadoVazio } from '@/shared/components/estados';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

interface Props {
  farmaco: CatalogoFarmacoDetalheResponse;
  podeEditar: boolean;
}

/**
 * As apresentações comerciais do fármaco — a marca com a concentração.
 *
 * Vive dentro da tela do fármaco, e não numa tela própria, pelo mesmo motivo
 * da grade de faixas do catálogo de exames: uma apresentação só faz sentido
 * junto do princípio ativo dela. "Vetoryl 30 mg" sozinho não diz que é
 * trilostano.
 *
 * ⚠️ Também aqui não existe apagar. `TratamentoVigencia` aponta para a
 * apresentação, e a que saiu de linha continua sendo a que aquele animal tomou.
 */
export function ApresentacoesDoFarmaco({ farmaco, podeEditar }: Props) {
  const queryClient = useQueryClient();

  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<ApresentacaoResponse | undefined>();
  const [aDesativar, setADesativar] = useState<ApresentacaoResponse | null>(null);

  const desativar = useCatalogoFarmacosControllerDesativarApresentacao();
  const atualizar = useCatalogoFarmacosControllerAtualizarApresentacao();

  async function recarregar() {
    await queryClient.invalidateQueries({
      queryKey: getCatalogoFarmacosControllerObterQueryKey(farmaco.id),
    });
    // A listagem mostra o número de apresentações ativas.
    await queryClient.invalidateQueries({
      queryKey: getCatalogoFarmacosControllerListarQueryKey(),
    });
  }

  async function confirmarDesativacao() {
    if (!aDesativar) return;
    try {
      await desativar.mutateAsync({ id: farmaco.id, apresentacaoId: aDesativar.id });
      await recarregar();
      toast.success(`"${aDesativar.rotulo}" desativada.`);
    } catch (erro) {
      toast.error(
        erro instanceof ApiError ? erro.message : 'Não foi possível desativar a apresentação.',
      );
    } finally {
      setADesativar(null);
    }
  }

  async function reativar(apresentacao: ApresentacaoResponse) {
    try {
      await atualizar.mutateAsync({
        id: farmaco.id,
        apresentacaoId: apresentacao.id,
        data: { ativo: true },
      });
      await recarregar();
      toast.success(`"${apresentacao.rotulo}" reativada.`);
    } catch (erro) {
      toast.error(
        erro instanceof ApiError ? erro.message : 'Não foi possível reativar a apresentação.',
      );
    }
  }

  return (
    <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
      <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
          Apresentações comerciais
        </h3>

        {podeEditar && (
          <Button
            size="sm"
            onClick={() => {
              setEmEdicao(undefined);
              setFormAberto(true);
            }}
          >
            <Plus className="w-4 h-4" />
            Nova apresentação
          </Button>
        )}
      </div>

      {farmaco.apresentacoes.length === 0 ? (
        <EstadoVazio
          icone={Package}
          titulo="Nenhuma apresentação cadastrada"
          descricao="Sem a marca e a concentração, o receituário não consegue transformar “1 comprimido” em dose."
        />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Apresentação</TableHead>
                <TableHead>Fabricante</TableHead>
                <TableHead>Concentração</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-20">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {farmaco.apresentacoes.map((apresentacao) => (
                <TableRow key={apresentacao.id}>
                  <TableCell>
                    {/* O `rotulo` vem pronto do servidor ("Vetoryl 30 mg"): é
                        ele que aparece no seletor do receituário, e mostrá-lo
                        aqui é a única forma de o admin ver o que o clínico vê. */}
                    <p className="font-medium text-foreground">{apresentacao.rotulo}</p>
                    {apresentacao.rotulo !== apresentacao.nomeComercial && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {apresentacao.nomeComercial}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {apresentacao.fabricante ?? '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {apresentacao.concentracaoValor !== null &&
                    apresentacao.concentracaoUnidade !== null
                      ? `${String(apresentacao.concentracaoValor).replace('.', ',')} ${apresentacao.concentracaoUnidade}`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {apresentacao.ativo ? (
                      <Badge variant="outline" className="text-emerald-600 border-emerald-600/40">
                        Ativa
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-destructive border-destructive/40">
                        Desativada
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Ações de ${apresentacao.rotulo}`}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {podeEditar && (
                          <>
                            <DropdownMenuItem
                              onClick={() => {
                                setEmEdicao(apresentacao);
                                setFormAberto(true);
                              }}
                            >
                              <Pencil className="w-4 h-4" />
                              Editar
                            </DropdownMenuItem>

                            {apresentacao.ativo ? (
                              <DropdownMenuItem
                                onClick={() => setADesativar(apresentacao)}
                                className="text-destructive focus:text-destructive"
                              >
                                <PowerOff className="w-4 h-4" />
                                Desativar
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => void reativar(apresentacao)}>
                                <Power className="w-4 h-4" />
                                Reativar
                              </DropdownMenuItem>
                            )}
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ApresentacaoFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        farmacoId={farmaco.id}
        {...(emEdicao ? { apresentacao: emEdicao } : {})}
      />

      <AlertDialog open={Boolean(aDesativar)} onOpenChange={(v) => !v && setADesativar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar &quot;{aDesativar?.rotulo}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              Ela sai do seletor do receituário, mas não é apagada: as vigências de tratamento que
              já apontam para esta apresentação continuam mostrando-a. Pode ser reativada a
              qualquer momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmarDesativacao()}>
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
